import React from 'react';
import PropTypes from 'prop-types';

import {Button, Box, Stack, Typography} from 'views/components';

import {translate, Translate} from 'views/module';

import NotificationStatusTag from './NotificationStatusTag';
import SubscribeUserDialog from '../dialogs/SubscribeUserDialog';
import ConfirmDialog from '../dialogs/ConfirmDialog';

const SmsNotification = (props) => {
  const {clientBackend, relationshipData, userNotificationChannels} = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState(null);
  const [confirmDialogProps, setConfirmDialogProps] = React.useState({});
  const [smsNumber, setSmsNumber] = React.useState(userNotificationChannels.smsNumber ?? '');
  const [errorMessage, setErrorMessage] = React.useState(translate('client.ClientDetail.Relationships.Notification.Dialogs.SmsHelperText.InappropriateLength'));

  React.useEffect(() => {
    setSmsNumber(userNotificationChannels.smsNumber ?? '');
  }, [userNotificationChannels.smsNumber]);

  const createUserSms = async (smsNumber) => {
    setIsLoading(true);
    const result = clientBackend.notification.createUserSms(
        relationshipData.relationshipRn,
        smsNumber,
        () => setIsLoading(false),
    );
    result ? setSmsNumber(smsNumber) : setSmsNumber('');
  };

  function attemptConversionToE164Format(input) {
    if (!input) return '';
    const digits = input.replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('1')) {
      return '+' + digits;
    } else if (digits.length === 10) {
      return '+1' + digits;
    } else {
      return '';
    }
  }

  const handleSubmitSmsNumber = (input) => {
    const convertedInput = attemptConversionToE164Format(input);
    if (!/^\+1\d{10}$/.test(convertedInput)) {
      setErrorMessage(translate('client.ClientDetail.Relationships.Notification.Dialogs.SmsHelperText.UnableToConvert'));
    } else {
      createUserSms(convertedInput);
      setShowDialog(false);
      setErrorMessage(translate('client.ClientDetail.Relationships.Notification.Dialogs.SmsHelperText.InappropriateLength'));
    }
  };

  const removeUserSms = async () => {
    setIsLoading(true);
    await clientBackend.notification.removeUserSms(
        relationshipData.relationshipRn,
        smsNumber,
        () => setIsLoading(false),
    );
  };

  const handleOpenDialog = () => {
    const smsNumber = relationshipData.contacts.find(
        (contact) => contact.type === 'Phone number',
    );
    const existingSmsNumber = smsNumber ? smsNumber.detail.trim() : '';
    setSmsNumber(existingSmsNumber);
    setShowDialog(true);
  };

  const handleConfirm = async () => {
    if (confirmAction) {
      await confirmAction();
    }
    setShowConfirmDialog(false);
  };

  const getButtonConfig = () => {
    const channelTypeTranslated = translate(
        'client.ClientDetail.Relationships.Notification.ChannelType.Sms',
    );

    if (userNotificationChannels.smsNumber) {
      return {
        text: translate(
            'client.ClientDetail.Relationships.Notification.Unsubscribe',
            {channelType: channelTypeTranslated},
        ),
        action: () => {
          setConfirmDialogProps({
            title: translate(
                'client.ClientDetail.Relationships.Notification.Dialogs.ConfirmUnsubscribeTitle',
            ),
            message: translate(
                'client.ClientDetail.Relationships.Notification.Dialogs.ConfirmUnsubscribeMessage',
            ),
            confirmText: translate(
                'client.ClientDetail.Relationships.Notification.Unsubscribe',
                {channelType: channelTypeTranslated},
            ),
          });
          setConfirmAction(() => removeUserSms);
          setShowConfirmDialog(true);
        },
      };
    } else {
      return {
        text: translate(
            'client.ClientDetail.Relationships.Notification.Subscribe',
            {channelType: channelTypeTranslated},
        ),
        action: handleOpenDialog,
      };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <Stack>
      <Typography
        variant='overline'
        sx={{
          fontWeight: 500,
        }}
      >
        <Translate expression='client.ClientDetail.Relationships.Notification.ChannelType.Sms' />
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
            <Box sx={{fontWeight: 500, fontSize: '14px'}}>{smsNumber}</Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <NotificationStatusTag userNotificationChannel={userNotificationChannels.smsNumber} />
            </Box>
          </Box>
        </Box>
        <Box sx={{display: 'flex', gap: 2}}>
          <Button
            onClick={buttonConfig.action}
            variant='contained'
            color='primary'
            disabled={isLoading}
          >
            {buttonConfig.text}
          </Button>
        </Box>
      </Box>
      {
        showDialog &&
        <SubscribeUserDialog
          open={showDialog}
          onClose={() => {
            setSmsNumber('');
            setShowDialog(false);
          }}
          onSubmit={handleSubmitSmsNumber}
          initialChannelValue={smsNumber}
          isLoading={isLoading}
          channelType='Sms'
          errorMessage={errorMessage}
          regex={/^\+?1?\s*(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/}
        />
      }
      {
        showConfirmDialog &&
        <ConfirmDialog
          open={showConfirmDialog}
          title={confirmDialogProps.title}
          message={confirmDialogProps.message}
          onCancel={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirm}
          confirmText={confirmDialogProps.confirmText}
          cancelText={translate(
              'client.ClientDetail.Relationships.Notification.Cancel',
          )}
          isLoading={isLoading}
        />
      }
    </Stack>
  );
};

SmsNotification.propTypes = {
  clientBackend: PropTypes.object.isRequired,
  userNotificationChannels: PropTypes.shape({
    email: PropTypes.string,
    smsNumber: PropTypes.string,
  }).isRequired,
  relationshipData: PropTypes.object.isRequired,
};

export default SmsNotification;
